import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, deleteDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { normalizeKey, normalizeType, keyRoot } from '../utils/normalize';
import AbcPreview from '../components/AbcPreview';
import { useUserPrefs } from '../UserPrefsProvider';
import { loadSetHybrid } from '../utils/loadSetHybrid';
import dexie from '../offlineDB';

export default function SetDetailPage() {
  const { user } = useAuth();
  const { prefs } = useUserPrefs();
  const { inputStyle, textStyle } = useUI();
  const { id: setId } = useParams();
  const location = useLocation();
const state = location.state || {};
const setTunes = state.setTunes || [];
const currentIndex = setTunes.findIndex(item => item.id === setId);
const parentSetTitle = state.setTitle || null;

  const navigate = useNavigate();
  const [setData, setSetData] = useState(null);
  const [allTunes, setAllTunes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [allSets, setAllSets] = useState([]);


  useEffect(() => {
    if (!user) return;

    const unsubSets = onSnapshot(collection(db, 'users', user.uid, 'sets'), snapshot => {
      setAllSets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubTunes = onSnapshot(collection(db, 'users', user.uid, 'tunes'), snapshot => {
      setAllTunes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

const fetchSet = async () => {
  const data = await loadSetHybrid(user, setId);
  if (data) setSetData(data);
};


    fetchSet();
    return () => {
      unsubTunes();
      unsubSets();
    };

  }, [user, setId]);

  useEffect(() => {
  if (!user || navigator.onLine) return;

  // Offline fallback
 dexie.sets.toArray().then(setList => {
    setAllSets(setList);
  });
  dexie.tunes.toArray().then(tuneList => {
    setAllTunes(tuneList);
  });
}, [user]);


const resolveTune = (stub) => {
  if (stub.type === 'set') {
    const match = allSets.find(s => s.id === stub.id);
    return match ? { ...match, type: 'set' } : { ...stub, type: 'set' };
  } else {
    const match = allTunes.find(t => t.id === stub.id);
    return match ? { ...match, type: 'tune' } : { ...stub, type: 'tune' };
  }
};


const handleDelete = async () => {
  if (!window.confirm('Are you sure you want to delete this set?')) return;

  try {
    if (navigator.onLine) {
      await deleteDoc(doc(db, 'users', user.uid, 'sets', setId));
    } else {
      await dexie.sets.delete(setId);
await dexie.pendingWrites.put({
  id: `${setId}__delete`,
  type: 'delete',
  collection: 'sets',
  docId: setId,
  data: {}
});

    }

    navigate('/sets');
  } catch (err) {
    console.error('Error deleting set:', err);
    alert('Failed to delete set.');
  }
};


if (!setData) {
  return (
    <PageWrapperAuto>
      <p style={{ textAlign: 'center' }}>
        ⚠️ Could not load set. You may be offline, not logged in, or the set was deleted.
      </p>
    </PageWrapperAuto>
  );
}

  return (
    <PageWrapperAuto>

      {parentSetTitle && (
  <div style={{ marginTop: '12px', marginBottom: '12px', textAlign: 'center' }}>
    <h2 style={{ ...textStyle, textTransform: 'uppercase', marginBottom: '8px' }}>
      Set: {parentSetTitle}
    </h2>
  </div>
)}

{setTunes.length > 1 && currentIndex !== -1 && (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 12px'
  }}>
    {currentIndex > 0 ? (
      <button className="big-button" onClick={() => {
        const prevItem = setTunes[currentIndex - 1];
        if (prevItem?.type === 'set') {
          navigate(`/sets/${prevItem.id}`, { state });
        } else {
          navigate(`/tune/${prevItem.id}`, {
            state: {
              tune: prevItem,
              setId,
              setTitle: parentSetTitle,
              setTunes
            }
          });
        }
      }}>← Prev</button>
    ) : <div style={{ width: '80px' }}></div>}

    <span className="text-scaled" style={{ color: 'var(--text-muted)' }}>
      {currentIndex + 1} of {setTunes.length}
    </span>

    {currentIndex < setTunes.length - 1 ? (
      <button className="big-button" onClick={() => {
        const nextItem = setTunes[currentIndex + 1];
        if (nextItem?.type === 'set') {
          navigate(`/sets/${nextItem.id}`, { state });
        } else {
          navigate(`/tune/${nextItem.id}`, {
            state: {
              tune: nextItem,
              setId,
              setTitle: parentSetTitle,
              setTunes
            }
          });
        }
      }}>Next →</button>
    ) : <div style={{ width: '80px' }}></div>}
  </div>
)}

      <h2>{setData.title}     
        <span style={{
      fontSize: '1rem',
      backgroundColor: '#228be6',
      color: 'white',
      padding: '2px 6px',
      paddingBottom: '2px',
      borderRadius: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      SET
    </span></h2>
      
      {/* Rating and Tags */}
        <div style={{ marginBottom: '0.75rem' }}>
          {setData.rating && (
            <div style={{ fontSize: '1rem', marginBottom: '1rem' }}>
              <strong>Rating: </strong> <span style={{ fontSize: '1.2rem' }}>{setData.rating}</span>
            </div>
          )}

      {Array.isArray(setData.customTags) && setData.customTags.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.25rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <strong>Tags: </strong> 
            {setData.customTags.map((tag, idx) => (
              <span
                key={idx}
                style={{
                  backgroundColor: '#eee',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: '#333',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {setData.notes && setData.notes.trim() !== '' && (
        <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
          <div
            onClick={() => setShowNotes(v => !v)}
            style={{
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              color: 'var(--text-primary)'
            }}
          >
            <span>{showNotes ? '▼' : '▶'} Notes:</span>
          </div>

          {showNotes && (
<div
  style={{
    marginTop: '0.5rem',
    display: 'flex',
    justifyContent: 'center',
  }}
>
  <div
    style={{
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      maxWidth: '400px',
      fontSize: '0.9rem',
      color: '#ccc',
      padding: '0 1rem',
    }}
  >
    {setData.notes}
  </div>
</div>

          )}
        </div>
      )}



        </div>

      <div style={{ marginBottom: '16px' }}>
        <button className="big-button" onClick={() => navigate(`/sets/${setId}/edit`)} style={{ marginRight: '10px' }}>Edit Set</button>
        <button className="big-button" onClick={handleDelete} >Delete Set</button>
      </div>

  


      <ul className="card-list">
        {(setData.tunes || []).map((stub, i) => {
          const tune = resolveTune(stub);
          return (
           <li
  key={i}
  className="card"
onClick={() => {
  if (tune.type === 'set') {
    navigate(`/sets/${tune.id}`);
  } else {
    const resolvedTunes = (setData.tunes || []).map(resolveTune);
    navigate(`/tune/${tune.id}`, {
      state: {
        tune,
        setId,
        setTitle: setData.title,
        setTunes: resolvedTunes,
      },
    });
  }
}}

  style={{
    cursor: 'pointer',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-primary)',
    border: '1px solid var(--card-border)',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '10px',
  }}
>
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
    }}
  >
    <div style={{ flex: '1 1 200px', minWidth: '160px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5ch' }}>
<strong style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
  {tune.title}
  {tune.type === 'set' && (
    <span style={{
      fontSize: '0.7rem',
      backgroundColor: '#228be6',
      color: 'white',
      padding: '2px 6px',
      borderRadius: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      SET
    </span>
  )}
</strong>


       {tune.type === 'set' ? (
        <span style={{ fontSize: '0.85rem', color: '#aaa' }}>
          – Includes:{' '}
          {Array.isArray(tune.tunes) && tune.tunes.length > 0
            ? tune.tunes.map(child => child.title).filter(Boolean).join(', ')
            : '0 tunes'}
        </span>

      ) : (
        <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
          – {[tune.type, tune.key].filter(Boolean).join(' / ')}
        </span>
      )}

      </div>

    {prefs?.showLyricsInSets && tune.lyrics && (
      <div
        style={{
          marginTop: '4px',
          fontSize: '0.85em',
          color: '#888',
          whiteSpace: 'pre-line',
          lineHeight: '1.2em',
          maxHeight: '2.6em',
          overflow: 'hidden',
        }}
      >
        {tune.lyrics.split('\n').slice(0, 2).join('\n')}
      </div>
    )}

    </div>

  {prefs?.showAbcInSets && tune.abc && (
    <div style={{ flex: '0 0 auto', overflow: 'hidden', height: '50px' }}>
      <AbcPreview abc={tune.abc} width={320} scale={0.55} />
    </div>
  )}

  </div>
</li>

          );
        })}
      </ul>
    </PageWrapperAuto>
  );
}
